import React, { useMemo } from "react";
import clsx from "clsx";

import {
  getStudentAnswersByQuestion,
  getLessonFrom,
  getIssueFrom,
  getQuestionFrom,
} from "../../../../libs/ActiveLearning";
import {
  IActiveLearningMaterialLessonsData,
  IActiveLearningStudentAnswers,
  IActiveLearningAllAnswers,
  IStudent,
} from "../../../../state";
import styles from "../LessonsAnswerData.module.scss";

type Lesson6Pros = {
  materialCode: string;
  lessonNo: number;
  activeLearningMaterialLessons: IActiveLearningMaterialLessonsData[];
  activeLearningAnswers:
    | IActiveLearningStudentAnswers
    | IActiveLearningAllAnswers
    | null;
  students: IStudent[];
};

export const Lesson6: React.FC<Lesson6Pros> = ({
  materialCode,
  lessonNo,
  activeLearningMaterialLessons,
  activeLearningAnswers,
  students,
}) => {
  const activeLearningMaterialLessonsByCode = useMemo(() => {
    return activeLearningMaterialLessons.filter(
      data => data.code === materialCode,
    );
  }, [activeLearningMaterialLessons, materialCode]);

  const titleL6 = useMemo(() => {
    return getLessonFrom(activeLearningMaterialLessonsByCode, lessonNo)?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL6I1 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 1)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL6I1Q1 = useMemo(() => {
    return getQuestionFrom(activeLearningMaterialLessonsByCode, lessonNo, 1, 1)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL6I2 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 2)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL6I2Q1 = useMemo(() => {
    return getQuestionFrom(activeLearningMaterialLessonsByCode, lessonNo, 2, 1)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL6I3 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 3)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL6I3Q1 = useMemo(() => {
    return getQuestionFrom(activeLearningMaterialLessonsByCode, lessonNo, 3, 1)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL6I3Q2 = useMemo(() => {
    return getQuestionFrom(activeLearningMaterialLessonsByCode, lessonNo, 3, 2)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL6I3Q3 = useMemo(() => {
    return getQuestionFrom(activeLearningMaterialLessonsByCode, lessonNo, 3, 3)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const answersL6I1Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      1,
      1,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL6I2Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      2,
      1,
    );

    return answers.map(answer => {
      const matchedStudent = students.find(
        student => student.id === answer.contents.to_student_id,
      );

      return {
        student_id: matchedStudent?.id,
        full_name: `${matchedStudent?.last_name} ${matchedStudent?.first_name}`,
        contents: answer.contents,
      };
    });
  }, [materialCode, activeLearningAnswers, lessonNo, students]);

  const answersL6I3Q1 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      3,
      1,
    );
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL6I3Q2 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      3,
      2,
    );
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL6I3Q3 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      3,
      3,
    );
  }, [materialCode, activeLearningAnswers, lessonNo]);

  return (
    <div className={styles.lesson}>
      <p className={styles.title}>
        Lesson{lessonNo < 10 ? `0${lessonNo}` : lessonNo} {titleL6}
      </p>
      <div className={styles.inner}>
        <div className={styles.work}>
          <p className={styles.no}>WORK01</p>
          <p className={styles.heading}>{titleL6I1}</p>
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>{titleL6I1Q1}</p>
            <p className={clsx(styles.answer, styles.high)}>{answersL6I1Q1}</p>
          </div>
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK02</p>
          <p className={styles.heading}>{titleL6I2}</p>
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>{titleL6I2Q1}</p>
            {answersL6I2Q1.length > 0 ? (
              answersL6I2Q1.map((answer, index) => (
                <div key={answer.student_id ?? index} className={styles.tree}>
                  <p className={styles.answer}>{answer.full_name}</p>
                  <ul className={styles.branchList}>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>コメント</span>
                      </div>
                      <ul className={styles.answer}>
                        <li className={styles.item}>{answer.contents.value}</li>
                      </ul>
                    </li>
                  </ul>
                </div>
              ))
            ) : (
              <div className={styles.question}>
                <div className={styles.tree}>
                  <p className={styles.answer}>回答データがありません。</p>
                  <ul className={styles.branchList}>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>コメント</span>
                      </div>
                      <ul className={styles.answer}>
                        <li className={styles.item}>
                          回答データがありません。
                        </li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK03</p>
          <p className={styles.heading}>{titleL6I3}</p>
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>{titleL6I3Q1}</p>
            <div className={styles.multiple}>
              <div className={styles.block}>
                <p className={styles.label}>良かった点</p>
                {answersL6I3Q1.length > 0 ? (
                  answersL6I3Q1.map(answer => (
                    <p
                      key={answer.id}
                      className={clsx(styles.answer, styles.high)}
                    >
                      {answer.contents.good}
                    </p>
                  ))
                ) : (
                  <p className={clsx(styles.answer, styles.high)}>
                    回答データがありません。
                  </p>
                )}
              </div>
              <div className={styles.block}>
                <p className={styles.label}>改善点</p>
                {answersL6I3Q1.length > 0 ? (
                  answersL6I3Q1.map(answer => (
                    <p
                      key={answer.id}
                      className={clsx(styles.answer, styles.high)}
                    >
                      {answer.contents.motto}
                    </p>
                  ))
                ) : (
                  <p className={clsx(styles.answer, styles.high)}>
                    回答データがありません。
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>{titleL6I3Q2}</p>
            <div className={styles.multiple}>
              <div className={styles.block}>
                <p className={styles.label}>良かった点</p>
                {answersL6I3Q2.length > 0 ? (
                  answersL6I3Q2.map(answer => (
                    <p
                      key={answer.id}
                      className={clsx(styles.answer, styles.high)}
                    >
                      {answer.contents.good}
                    </p>
                  ))
                ) : (
                  <p className={clsx(styles.answer, styles.high)}>
                    回答データがありません。
                  </p>
                )}
              </div>
              <div className={styles.block}>
                <p className={styles.label}>改善点</p>
                {answersL6I3Q2.length > 0 ? (
                  answersL6I3Q2.map(answer => (
                    <p
                      key={answer.id}
                      className={clsx(styles.answer, styles.high)}
                    >
                      {answer.contents.motto}
                    </p>
                  ))
                ) : (
                  <p className={clsx(styles.answer, styles.high)}>
                    回答データがありません。
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>{titleL6I3Q3}</p>
            <div className={styles.multiple}>
              <div className={styles.block}>
                <p className={styles.label}>良かった点</p>
                {answersL6I3Q3.length > 0 ? (
                  answersL6I3Q3.map(answer => (
                    <p
                      key={answer.id}
                      className={clsx(styles.answer, styles.high)}
                    >
                      {answer.contents.good}
                    </p>
                  ))
                ) : (
                  <p className={clsx(styles.answer, styles.high)}>
                    回答データがありません。
                  </p>
                )}
              </div>
              <div className={styles.block}>
                <p className={styles.label}>改善点</p>
                {answersL6I3Q3.length > 0 ? (
                  answersL6I3Q3.map(answer => (
                    <p
                      key={answer.id}
                      className={clsx(styles.answer, styles.high)}
                    >
                      {answer.contents.motto}
                    </p>
                  ))
                ) : (
                  <p className={clsx(styles.answer, styles.high)}>
                    回答データがありません。
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Lesson6;
